import { isNil } from 'lodash';
import { DateRangeField } from '@shared/data/entity/fields/DateRangeField';
import {
  isAddressComponent,
  isCombinedTagsComponent,
  isDateComponent,
  isDateRangeComponent,
  isEmailComponent,
  isEntityBatchComponent,
  isFileComponent,
  isGeolocationComponent,
  isNumberComponent,
  isPersonComponent,
  isRelatedCardComponent,
  isRelatedCardLookupComponent,
  isScheduledAutomationComponent,
  isSignatureComponent,
  isSubformComponent,
  isTagComponent,
  isTextComponent,
  isTimeTrackerComponent,
  isTodoComponent,
  isUrlComponent,
} from '@shared/scenes/workflows/utils';
import { User } from '@shared/types/user';
import { AddressField } from '../entity/fields/AddressField';
import { CombinedTagsField } from '../entity/fields/CombinedTagsField';
import { DateField } from '../entity/fields/DateField';
import { EmailField } from '../entity/fields/EmailField';
import { EntityBatchField } from '../entity/fields/EntityBatchField';
import { Field } from '../entity/fields/Field';
import { FileField } from '../entity/fields/FileField';
import { GenericUntypedJsonBlobField } from '../entity/fields/GenericUntypedJsonBlobField';
import { GeolocationField } from '../entity/fields/GeolocationField';
import { NumberField } from '../entity/fields/NumberField';
import { PersonField } from '../entity/fields/PersonField';
import { RelatedEntityField } from '../entity/fields/RelatedEntityField';
import { RelatedLookupField } from '../entity/fields/RelatedLookupField';
import { ScheduledAutomationField } from '../entity/fields/ScheduledAutomationField';
import { SignatureField } from '../entity/fields/SignatureField';
import { SubformField } from '../entity/fields/SubformField';
import { TagField } from '../entity/fields/TagField';
import { TextField } from '../entity/fields/TextField';
import { TimeTrackerField } from '../entity/fields/TimeTrackerField';
import { TodoField } from '../entity/fields/TodoField';
import { UrlField } from '../entity/fields/UrlField';

interface BuildCardFieldProps {
  entity: MBAPI.WorkflowEntity | null;
  component: MBAPI.WorkflowTemplate_components;
  refs: MBAPI.ListWorkflowEntitiesQuery_list_refs;
  defaultData?: any;
  currentUser?: User;
}

export function buildCardField({
  entity,
  component,
  refs,
  defaultData,
  currentUser,
}: BuildCardFieldProps): Field | null {
  const data = entity?.fields?.[component.id] ?? defaultData;

  if (isRelatedCardLookupComponent(component) && entity) {
    return RelatedLookupField(entity, component, refs);
  }
  if (isCombinedTagsComponent(component) && entity) {
    return new CombinedTagsField(component.componentIds, entity);
  }
  if (isDateRangeComponent(component)) {
    const startDate = entity?.fields?.[component.startDateComponentId];
    const endDate = entity?.fields?.[component.endDateComponentId];
    return DateRangeField.fromFieldJson({ start: startDate, end: endDate });
  }

  if (isNil(data)) {
    return null;
  }

  if (isNumberComponent(component)) {
    return NumberField.fromFieldJson(data, component.format);
  }
  if (isTagComponent(component) && component.options) {
    return TagField.fromFieldJson(
      data,
      component.id,
      component.options,
      component.multiselect ?? false,
      component.addAdditionalValues ?? false,
    );
  }
  if (isAddressComponent(component)) {
    return AddressField.fromFieldJson(data);
  }
  if (isDateComponent(component)) {
    return DateField.fromFieldJson(data);
  }
  if (isEntityBatchComponent(component)) {
    return EntityBatchField.fromFieldJson(data, refs.recurringEntitiesScheduleRefs);
  }
  if (isEmailComponent(component)) {
    return EmailField.fromFieldJson(data);
  }
  if (isFileComponent(component)) {
    return FileField.fromFieldJson(data);
  }
  if (isGeolocationComponent(component)) {
    return GeolocationField.fromFieldJson(data);
  }
  if (isPersonComponent(component)) {
    return PersonField.fromFieldJson(data, refs.personRefs, currentUser);
  }
  if (isRelatedCardComponent(component)) {
    return RelatedEntityField.fromFieldJson(
      data,
      refs.workflowEntityRefs,
      refs.workflowTemplateRefs,
    );
  }
  if (isScheduledAutomationComponent(component)) {
    return ScheduledAutomationField.fromFieldJson(data);
  }
  if (isSignatureComponent(component)) {
    return SignatureField.fromFieldJson(data);
  }
  if (isSubformComponent(component)) {
    return SubformField.fromFieldJson(data, refs.workflowTemplateRefs);
  }
  if (isTextComponent(component)) {
    return TextField.fromFieldJson(data, { multiline: component.multiline });
  }
  if (isTimeTrackerComponent(component)) {
    return TimeTrackerField.fromFieldJson(data);
  }
  if (isTodoComponent(component)) {
    return TodoField.fromFieldJson(data);
  }
  if (isUrlComponent(component)) {
    return UrlField.fromFieldJson(data);
  }

  // if we have data for this component id, but no other component type matches,
  // return a generic field
  return new GenericUntypedJsonBlobField(data);
}
